import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Truck, Plus, Check, X, Pencil, Trash2, Mail, Phone, CalendarClock, Loader2, AlertCircle, Landmark,
} from "lucide-react";

const WEEKDAYS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];
const EMPTY = { name: "", contact_person: "", email: "", phone: "", note: "", weekday: "none", bank_account: "" };

const fmtDate = (iso) => {
  if (!iso) return "aldrig";
  const [y, m, d] = iso.split("-");
  return `${parseInt(d, 10)}/${parseInt(m, 10)}-${y}`;
};

export function SuppliersBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/suppliers"); setRows(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(EMPTY); setEditId(null); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Indtast et leverandørnavn"); return; }
    const body = { ...form, weekday: form.weekday === "none" ? null : parseInt(form.weekday, 10) };
    try {
      if (editId) { await api.put(`/suppliers/${editId}`, body); toast.success("Leverandør opdateret"); }
      else { await api.post("/suppliers", body); toast.success("Leverandør tilføjet"); }
      resetForm(); await load();
    } catch { toast.error("Kunne ikke gemme"); }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setForm({ name: s.name, contact_person: s.contact_person || "", email: s.email || "",
      phone: s.phone || "", note: s.note || "", weekday: s.weekday == null ? "none" : String(s.weekday), bank_account: s.bank_account || "" });
  };

  const markContacted = async (s) => {
    try { await api.post(`/suppliers/${s.id}/contacted`); toast.success(`${s.name} markeret som kontaktet`); await load(); }
    catch { toast.error("Kunne ikke opdatere"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Slet leverandøren "${s.name}"?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Leverandør slettet"); await load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const mailtoHref = (s) => {
    const subject = encodeURIComponent("Opdateret vareliste");
    const body = encodeURIComponent(`Hej ${s.contact_person || ""}\n\nKan jeg få tilsendt en opdateret liste over jeres varer og priser?\n\nMange tak.\nMvh`);
    return `mailto:${s.email}?subject=${subject}&body=${body}`;
  };

  const sorted = [...rows].sort((a, b) => (a.due === b.due ? a.name.localeCompare(b.name, "da") : a.due ? -1 : 1));
  const dueCount = rows.filter((r) => r.due).length;

  return (
    <div className="space-y-4" data-testid="suppliers-board">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Truck className="w-4 h-4" />
        <span>Leverandører · kontaktregister &amp; mandags-påmindelse om opdateret vareliste</span>
      </div>

      {/* Due banner */}
      {dueCount > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-3 flex items-center gap-2.5" data-testid="suppliers-due-banner">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800">
            <span className="font-semibold">{dueCount}</span> {dueCount === 1 ? "leverandør mangler" : "leverandører mangler"} at blive kontaktet denne uge — husk at bede om en opdateret liste.
          </span>
        </div>
      )}

      {/* Add / edit form */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="supplier-editor">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">{editId ? "Redigér leverandør" : "Tilføj leverandør"}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
          <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Leverandørnavn *" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 lg:col-span-2" data-testid="supplier-name-input" />
          <Input value={form.contact_person} onChange={(e) => setField("contact_person", e.target.value)} placeholder="Kontaktperson" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="supplier-contact-input" />
          <Input value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="E-mail" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="supplier-email-input" />
          <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="Telefon" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="supplier-phone-input" />
          <Input value={form.bank_account} onChange={(e) => setField("bank_account", e.target.value)} placeholder="Kontonr. (bank)" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid="supplier-bank-input" />
          <Select value={form.weekday} onValueChange={(v) => setField("weekday", v)}>
            <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="supplier-weekday-select"><SelectValue placeholder="Kontaktdag" /></SelectTrigger>
            <SelectContent className="rounded-lg bg-white border-[#e2e8f0] text-slate-700">
              <SelectItem value="none">Ingen fast dag</SelectItem>
              {WEEKDAYS.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={form.note} onChange={(e) => setField("note", e.target.value)} placeholder="Note (valgfri)" onKeyDown={(e) => e.key === "Enter" && submit()} className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 lg:col-span-5" data-testid="supplier-note-input" />
          <div className="flex gap-2">
            <Button onClick={submit} className="h-9 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] gap-1.5 flex-1" data-testid="supplier-save-btn">
              {editId ? <><Check className="w-4 h-4" /> Gem</> : <><Plus className="w-4 h-4" /> Tilføj</>}
            </Button>
            {editId && <Button variant="ghost" size="icon" onClick={resetForm} className="h-9 w-9 rounded-lg text-slate-500" data-testid="supplier-cancel-btn"><X className="w-4 h-4" /></Button>}
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-8 text-center text-sm text-slate-500" data-testid="suppliers-empty">
          Ingen leverandører endnu. Tilføj din første ovenfor.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="suppliers-list">
          {sorted.map((s) => (
            <div key={s.id} className={`rounded-2xl bg-white border p-4 ${s.due ? "border-amber-300 ring-1 ring-amber-100" : "border-[#e2e8f0]"}`} data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-slate-900 truncate">{s.name}</span>
                    {s.due ? (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`supplier-due-${s.id}`}>Skal kontaktes</span>
                    ) : (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Kontaktet</span>
                    )}
                    {s.weekday != null && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {WEEKDAYS[s.weekday]}</span>
                    )}
                  </div>
                  {s.contact_person && <p className="text-sm text-slate-600 mt-0.5">{s.contact_person}</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(s)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-[#f1f5f9]" data-testid={`supplier-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(s)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`supplier-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-1.5 text-sm">
                {s.email && (
                  <a href={mailtoHref(s)} className="inline-flex items-center gap-2 text-sky-700 hover:underline w-fit" data-testid={`supplier-mail-${s.id}`}>
                    <Mail className="w-3.5 h-3.5" /> {s.email}
                  </a>
                )}
                {s.phone && <span className="inline-flex items-center gap-2 text-slate-600"><Phone className="w-3.5 h-3.5" /> {s.phone}</span>}
                {s.bank_account && <span className="inline-flex items-center gap-2 text-slate-600"><Landmark className="w-3.5 h-3.5" /> {s.bank_account}</span>}
              </div>

              {s.note && <p className="mt-2 text-sm text-slate-500 whitespace-pre-wrap">{s.note}</p>}

              <div className="mt-3 pt-3 border-t border-[#f1f5f9] flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">Sidst kontaktet: <span className="font-medium text-slate-700">{fmtDate(s.last_contacted)}</span></span>
                <Button size="sm" variant={s.due ? "default" : "outline"} onClick={() => markContacted(s)} className={`h-8 rounded-lg gap-1.5 ${s.due ? "bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120]" : "bg-white border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6]"}`} data-testid={`supplier-contacted-${s.id}`}>
                  <Check className="w-4 h-4" /> Marker kontaktet
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SuppliersBoard;
